import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Box,
  Typography,
  Paper,
  TextField,
  Select,
  MenuItem,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  TablePagination,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  RadioGroup,
  Radio,
  Chip,
} from "@mui/material";
import * as XLSX from "xlsx";
import {
  Save as SaveIcon,
  Refresh as RefreshIcon,
  CloudUpload as UploadIcon,
  Edit as EditIcon,
  Block as BlockIcon,
  CheckCircle as EnableIcon,
  Visibility as ViewIcon,
  Download as DownloadIcon,
} from "@mui/icons-material";
import {
  getAllowedLabTypes,
  getLabTypeName,
  getInitialLabTypeId,
  getFirstLabTypeId,
  shouldDisableDropdown,
  LAB_TYPES,
} from "../../../../utils/labTypeFilter";

const ScopeMaster = () => {
  const API_BASE_URL =
    process.env.REACT_APP_API_URL || "http://localhost:5000/api";

  // Form state
  const [formData, setFormData] = useState({
    scopeid: null,
    labtypeid: "",
    disciplineid: "",
    scopetype: "",
    ptavailable: false,
    materialstested: "",
    componentparametertested: "",
    testmethodspecification: "",
    subgroup: "",
    groupid: "",
    subgroupid: "",
  });

  // Dropdown states
  const [disciplines, setDisciplines] = useState([]);
  const [subGroups, setSubGroups] = useState([]);
  const [groups, setGroups] = useState([]);
  const [scopeData, setScopeData] = useState([]);
  const [error, setError] = useState("");

  // Scope Type options
  const scopeTypeOptions = ["Qualitative", "Quantitative", "Calculated"];

  // Loading and messages
  const [loading, setLoading] = useState(false);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");

  // Filter state
  const [filterLabType, setFilterLabType] = useState("");
  const [filterDiscipline, setFilterDiscipline] = useState("");
  const [filterGroup, setFilterGroup] = useState("");
  const [filterSubGroup, setFilterSubGroup] = useState("");
  const [filterSubGroups, setFilterSubGroups] = useState([]);
  const [searchText, setSearchText] = useState("");

  // Dialog states
  const [isEditMode, setIsEditMode] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [enableDialogOpen, setEnableDialogOpen] = useState(false);
  const [enableRow, setEnableRow] = useState(null);
  const [disabledRow, setDisabledRow] = useState(null);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedScope, setSelectedScope] = useState(null);
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkLabType, setBulkLabType] = useState("");
  const [bulkDiscipline, setBulkDiscipline] = useState("");
  const [bulkGroup, setBulkGroup] = useState("");
  const [bulkSubGroup, setBulkSubGroup] = useState("");
  const [bulkScopeType, setBulkScopeType] = useState("");
  const [bulkPTAvailable, setBulkPTAvailable] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkGroups, setBulkGroups] = useState([]);
  const [bulkSubGroups, setBulkSubGroups] = useState([]);

  // Pagination state - server-side
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [totalCount, setTotalCount] = useState(0);
  const [filterComponent, setFilterComponent] = useState("");
  const [filterMaterial, setFilterMaterial] = useState("");
  const [filterTestMethod, setFilterTestMethod] = useState("");
  const [filterTestCode, setFilterTestCode] = useState("");

  // Lab count state
  const [labCounts, setLabCounts] = useState({});
  const [labCountsLoading, setLabCountsLoading] = useState(false);
  const [labsDialogOpen, setLabsDialogOpen] = useState(false);
  const [labsDialogScope, setLabsDialogScope] = useState(null);
  const [labsList, setLabsList] = useState([]);
  const [labsListLoading, setLabsListLoading] = useState(false);

  // Disabled scopes state
  const [disabledScopeData, setDisabledScopeData] = useState([]);
  const [disabledScopeLoading, setDisabledScopeLoading] = useState(false);

  // Get filtered lab types using utility
  const filteredLabTypes = getAllowedLabTypes(LAB_TYPES);

  // Fetch disciplines when lab type changes
  useEffect(() => {
    if (formData.labtypeid) {
      fetchDisciplines(formData.labtypeid);
    }
  }, [formData.labtypeid]);

  // Fetch groups when lab type and discipline changes
  useEffect(() => {
    if (formData.labtypeid && formData.disciplineid) {
      fetchGroups(formData.labtypeid, formData.disciplineid);
    }
  }, [formData.labtypeid, formData.disciplineid]);

  // Fetch sub-groups when discipline changes
  useEffect(() => {
    if (formData.labtypeid && formData.disciplineid && formData.groupid) {
      fetchSubGroups(formData.labtypeid, formData.disciplineid, formData.groupid);
    }
  }, [formData.labtypeid, formData.disciplineid, formData.groupid]);

  // Fetch filter sub-groups when filter lab type, discipline, and group change
  useEffect(() => {
    if (filterLabType && filterDiscipline && filterGroup) {
      fetchSubGroupsForFilter(filterLabType, filterDiscipline, filterGroup);
    } else {
      setFilterSubGroups([]);
      setFilterSubGroup('');
    }
  }, [filterLabType, filterDiscipline, filterGroup]);

  const handleExportExcel = async () => {
    try {
      setLoading(true);

      const params = {
        page: 1,
        limit: 1000000,
        ...(filterLabType && { labTypeId: filterLabType }),
        ...(filterDiscipline && { disciplineId: filterDiscipline }),
        ...(filterGroup && { groupId: filterGroup }),
        ...(filterSubGroup && { subGroupId: filterSubGroup }),
        ...(filterComponent && { component: filterComponent }),
        ...(filterMaterial && { material: filterMaterial }),
        ...(filterTestMethod && { testMethod: filterTestMethod }),
        ...(filterTestCode && { search: filterTestCode }),
      };

      const response = await axios.get(
        `${API_BASE_URL}/admin-master/scope-master`,
        { params },
      );

      if (response.data.status !== "success") {
        setError("Failed to export data");
        return;
      }

      const allData = response.data.data || [];


      const exportData = allData.map((row, index) => {
        const cabType = LAB_TYPES.find(
          (c) => String(c.id) === String(row.LabTypeid),
        );

        return {
          "S.No": index + 1,
          "CAB Type": cabType?.name || "N/A",
          "Test Code": row.TestCode || "N/A",
          "Discipline": row.DisciplineName || "N/A",
          ...((filterLabType===1 || filterLabType===2) && { "Group": row.GroupName || "NA" }),
          ...((filterLabType===1 || filterLabType===2) && { "Sub-Group": row.SubGroupName || "NA" }),
          "Component/Parameter Tested": row.TypeOfTest || "N/A",
          "Materials or Products Tested": row.GroupTested || "N/A",
          "Test Method Specification": row.ScopeStandard || "N/A",
          "Scope Type": row.Category || "N/A",
          "PT Available":
            row.PTAvailable === "1" || row.PTAvailable === "Yes" ? "Yes" : "No",
          "No. of Labs": row.LabCount || 0,
          "Status": String(row.IsActive) === "0" ? "Disabled" : "Enabled",
          "Created On": row.InsertDate
            ? new Date(row.InsertDate).toLocaleDateString()
            : "N/A",
        };
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();

      XLSX.utils.book_append_sheet(workbook, worksheet, "ScopeData");

      const timestamp = new Date()
        .toISOString()
        .slice(0, 19)
        .replace(/[:T]/g, "-");
      XLSX.writeFile(workbook, `Scope_Master_${timestamp}.xlsx`);
    } catch (err) {
      console.error(err);
      setError("Error exporting Excel");
    } finally {
      setLoading(false);
    }
  };

  // Fetch scope data when pagination or filters change
  useEffect(() => {
    if (filterLabType) {
      fetchScopeList();
    }
  }, [
    page,
    rowsPerPage,
    filterLabType,
    filterDiscipline,
    filterGroup,
    filterSubGroup,
    filterTestCode,
    filterComponent,
    filterMaterial,
    filterTestMethod,
  ]);

  // Fetch disabled scopes when any filter changes
  useEffect(() => {
    fetchDisabledScopes();
  }, [
    filterLabType,
    filterDiscipline,
    filterGroup,
    filterSubGroup,
    filterTestCode,
    filterComponent,
    filterMaterial,
    filterTestMethod,
  ]);

  // Debounce search text input
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      if (searchText !== undefined) {
        setPage(0);
      }
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchText]);

  // Fetch bulk groups when bulk lab type and discipline changes
  useEffect(() => {
    if (bulkLabType && bulkDiscipline) {
      fetchBulkGroups(bulkLabType, bulkDiscipline);
    } else {
      setBulkGroups([]);
      setBulkGroup("");
    }
  }, [bulkLabType, bulkDiscipline]);

  // Fetch bulk sub groups when bulk lab type and discipline changes
  useEffect(() => {
    if (bulkLabType && bulkDiscipline && bulkGroup) {
      fetchBulkSubGroups(bulkLabType, bulkDiscipline, bulkGroup);
    } else {
      setBulkSubGroups([]);
      setBulkSubGroup("");
    }
  }, [bulkLabType, bulkDiscipline, bulkGroup]);

  // Get labTypeIds from localStorage and set initial labTypeId if only one is allowed
  useEffect(() => {
    const storedLabTypeIds = localStorage.getItem("labTypeIds");
    const storedLabTypeId = localStorage.getItem("labTypeId");

    let defaultLabTypeId = null;

    if (storedLabTypeIds) {
      const allowedIds = storedLabTypeIds
        .split(",")
        .map((id) => parseInt(id.trim()))
        .filter((id) => !isNaN(id));

      if (allowedIds.length > 0) {
        defaultLabTypeId = allowedIds[0];
        const labTypeName = getLabTypeName(defaultLabTypeId);

        setFormData((prev) => ({
          ...prev,
          labtypeid: defaultLabTypeId,
        }));
        setBulkLabType(defaultLabTypeId);
        setFilterLabType(defaultLabTypeId);
      }
    } else if (storedLabTypeId) {
      defaultLabTypeId = Number(storedLabTypeId);
      const labTypeName = getLabTypeName(defaultLabTypeId);

      setFormData((prev) => ({
        ...prev,
        labtypeid: defaultLabTypeId,
      }));
      setBulkLabType(defaultLabTypeId);
      setFilterLabType(defaultLabTypeId);
    }
  }, []);

  // Fetch scope list with pagination and filtering
  const fetchScopeList = async () => {
    try {
      setLoading(true);

      const params = {
        page: page + 1,
        limit: rowsPerPage,
        ...(filterLabType && { labTypeId: filterLabType }),
        ...(filterDiscipline && { disciplineId: filterDiscipline }),
        ...(filterGroup && { groupId: filterGroup }),
        ...(filterSubGroup && { subGroupId: filterSubGroup }),
        ...(filterComponent && { component: filterComponent }),
        ...(filterMaterial && { material: filterMaterial }),
        ...(filterTestMethod && { testMethod: filterTestMethod }),
        ...(filterTestCode && { search: filterTestCode }),
      };

      const response = await axios.get(
        `${API_BASE_URL}/admin-master/scope-master`,
        {
          params,
        },
      );

      if (response.data.status === "success") {
        const freshData = (response.data.data || []).filter(
          (row) => String(row.IsActive) !== "0",
        );
        setScopeData(freshData);
        setTotalCount(response.data.totalCount || 0);
        setError("");
        // fetchLabCounts(freshData);
      } else {
        setError("Failed to fetch scope list");
        setScopeData([]);
        setTotalCount(0);
      }
    } catch (err) {
      console.error("Error fetching scope:", err);
      setError(err.response?.data?.message || "Error fetching scope list");
      setScopeData([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  // Fetch lab counts for visible scope rows
  // const fetchLabCounts = async (data) => {
  //   if (!data || data.length === 0) return;
  //   const ids = data.map((s) => s.scopeid).filter(Boolean).join(",");
  //   if (!ids) return;
  //   try {
  //     setLabCountsLoading(true);
  //     const response = await axios.get(
  //       `${API_BASE_URL}/admin-master/scope-master/labs-count`,
  //       { params: { scopeIds: ids } },
  //     );
  //     if (response.data.status === "success") {
  //       setLabCounts((prev) => ({ ...prev, ...response.data.data }));
  //     }
  //   } catch (err) {
  //     console.error("Error fetching lab counts:", err);
  //   } finally {
  //     setLabCountsLoading(false);
  //   }
  // };

  const fetchDisabledScopes = async () => {
    if (!filterLabType) return;
    try {
      setDisabledScopeLoading(true);
      const params = {
        page: 1,
        limit: 10000,
        isActive: 0,
        ...(filterLabType && { labTypeId: filterLabType }),
        ...(filterDiscipline && { disciplineId: filterDiscipline }),
        ...(filterGroup && { groupId: filterGroup }),
        ...(filterSubGroup && { subGroupId: filterSubGroup }),
        ...(filterComponent && { component: filterComponent }),
        ...(filterMaterial && { material: filterMaterial }),
        ...(filterTestMethod && { testMethod: filterTestMethod }),
        ...(filterTestCode && { search: filterTestCode }),
      };
      const response = await axios.get(`${API_BASE_URL}/admin-master/scope-master`, { params });
      if (response.data.status === "success") {
        const allData = response.data.data || [];
        const disabled = allData.filter(
          (row) => String(row.IsActive) === "0",
        );
        setDisabledScopeData(disabled);
      }
    } catch (err) {
      console.error("Error fetching disabled scopes:", err);
    } finally {
      setDisabledScopeLoading(false);
    }
  };

  const handleViewLabs = async (scope) => {
    setLabsDialogScope(scope);
    setLabsDialogOpen(true);
    setLabsListLoading(true);
    setLabsList([]);
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin-master/scope-master/${scope.scopeid}/labs`,
      );
      if (response.data.status === "success") {
        setLabsList(response.data.labs || []);
      }
    } catch (err) {
      console.error("Error fetching labs list:", err);
    } finally {
      setLabsListLoading(false);
    }
  };

  // Fetch disciplines by lab type
  const fetchDisciplines = async (labTypeId) => {
    try {
      const response = await axios.get(
        `${API_BASE_URL}/admin-master/disciplines/${labTypeId}`,
      );

      if (response.data.status === "success") {
        setDisciplines(response.data.data || []);
      } else {
        setDisciplines([]);
      }
    } catch (err) {
      console.error("Error fetching disciplines:", err);
      setDisciplines([]);
    }
  };

  // Fetch groups by lab type and discipline
  const fetchGroups = async (labTypeId, disciplineId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/groups`, {
        params: { labTypeId, disciplineId },
      });
      if (response.data.status === "success") {
        setGroups(response.data.data || []);
      } else {
        setGroups([]);
      }
    } catch (err) {
      console.error("Error fetching groups:", err);
      setGroups([]);
    }
  };

  // Fetch bulk groups by lab type and discipline
  const fetchBulkGroups = async (labTypeId, disciplineId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/groups`, {
        params: { labTypeId, disciplineId },
      });
      if (response.data.status === "success") {
        setBulkGroups(response.data.data || []);
      } else {
        setBulkGroups([]);
      }
    } catch (err) {
      console.error("Error fetching bulk groups:", err);
      setBulkGroups([]);
    }
  };

  // Fetch sub-groups by discipline and group
  const fetchSubGroups = async (labTypeId, disciplineId, groupId) => {
    try {
      const response = await axios.get( `${API_BASE_URL}/admin-master/subgroups`, {
          params: { labTypeId, disciplineId, groupId },
        }
      );

      if (response.data.status === "success") {
        setSubGroups(response.data.data || []);
      } else {
        setSubGroups([]);
      }
    } catch (err) {
      console.error("Error fetching sub-groups:", err);
      setSubGroups([]);
    }
  };

  // Fetch sub-groups for filter dropdown
  const fetchSubGroupsForFilter = async (labTypeId, disciplineId, groupId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/subgroups`, {
        params: { labTypeId, disciplineId, groupId },
      });
      if (response.data.status === "success") {
        setFilterSubGroups(response.data.data || []);
      } else {
        setFilterSubGroups([]);
      }
    } catch (err) {
      console.error("Error fetching filter sub-groups:", err);
      setFilterSubGroups([]);
    }
  };

  // Fetch bulk sub groups by lab type, discipline, and group
  const fetchBulkSubGroups = async (labTypeId, disciplineId, groupId) => {
    try {
      const response = await axios.get(`${API_BASE_URL}/admin-master/subgroups`, {
        params: { labTypeId, disciplineId, groupId },
      });
      if (response.data.status === "success") {
        setBulkSubGroups(response.data.data || []);
      } else {
        setBulkSubGroups([]);
      }
    } catch (err) {
      console.error("Error fetching bulk sub groups:", err);
      setBulkSubGroups([]);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === "checkbox" ? checked : value,
    });
  };

  // Handle form submission (Create/Update)
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!formData.labtypeid || !formData.disciplineid || !formData.scopetype) {
      setError("Laboratory Type, Discipline Name, and Scope Type are required");
      return;
    }

    if (formData.labtypeid === 1 || formData.labtypeid === 2) {
      if (!formData.groupid) {
        setError("Group is required for the selected Laboratory Type");
        return;
      }
    }

    if (formData.labtypeid === 1) {
      if (!formData.subgroupid) {
        setError("Sub-Group is required for the selected Laboratory Type");
        return;
      }
    }

    try {
      setLoading(true);
      setError("");

      const payload = {
        labtypeid: Number(formData.labtypeid),
        disciplineid: Number(formData.disciplineid),
        disciplineName:
          disciplines.find(
            (d) => Number(d.id) === Number(formData.disciplineid),
          )?.disciplineName || "",
        scopetype: formData.scopetype,
        ptavailable: formData.ptavailable,
        materialstested: formData.materialstested,
        componentparametertested: formData.componentparametertested,
        testmethodspecification: formData.testmethodspecification,
        subgroup: formData.subgroup,
        groupid: formData.groupid ? Number(formData.groupid) : null,
        subgroupid: formData.subgroupid ? Number(formData.subgroupid) : null,
        isactive: 1,
        updatedBy: localStorage.getItem("orgId") || "0",
      };

      let response;
      let url = `${API_BASE_URL}/admin-master/scope-master`;
      if (isEditMode && formData.scopeid) {
        response = await axios.put(`${url}/${formData.scopeid}`, payload);
      } else {
        response = await axios.post(url, payload);
      }

      if (response.data.status === "success") {
        setSuccessMessage(
          isEditMode
            ? "Scope updated successfully!"
            : "Scope created successfully!",
        );
        handleReset();
        fetchScopeList();
        setIsEditMode(false);
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(response.data.message || "Failed to save scope");
      }
    } catch (err) {
      console.error("Error saving scope:", err);
      setError(err.response?.data?.message || "Error saving scope");
    } finally {
      setLoading(false);
    }
  };

  // Handle form reset
  const handleReset = () => {
    const storedLabTypeId = localStorage.getItem("labTypeId");
    setFormData({
      scopeid: null,
      labtypeid: storedLabTypeId ? Number(storedLabTypeId) : "",
      disciplineid: "",
      scopetype: "",
      ptavailable: false,
      materialstested: "",
      componentparametertested: "",
      testmethodspecification: "",
      subgroup: "",
      groupid: "",
    });
    setIsEditMode(false);
    setDisciplines([]);
    setSubGroups([]);
    setGroups([]);
  };

  // Handle edit
  const handleEdit = async (scope) => {
    setFormData({
      scopeid: scope.scopeid,
      labtypeid: scope.LabTypeid,
      disciplineid: scope.disciplineid,
      scopetype: scope.Category,
      ptavailable: scope.PTAvailable || false,
      materialstested: scope.GroupTested || "",
      componentparametertested: scope.TypeOfTest || "",
      testmethodspecification: scope.ScopeStandard || "",
      subgroup: scope.subgroup || "",
      groupid: scope.Groupid ? String(scope.Groupid) : "",
      subgroupid: scope.SubGroupid ? String(scope.SubGroupid) : "",
    });
    setIsEditMode(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Handle delete
  const handleDeleteClick = (disabledRow) => {
    setDisabledRow(disabledRow);
    setDeleteDialogOpen(true);
  };
  const handleDeleteConfirm = async () => {
    if (!disabledRow) return;
    try {
      setLoading(true);
      const payload = {
        labtypeid: Number(disabledRow.LabTypeid),
        disciplineid: Number(disabledRow.disciplineid),
        disciplineName: disabledRow.DisciplineName || "",
        scopetype: disabledRow.Category,
        ptavailable: disabledRow.PTAvailable,
        materialstested: disabledRow.GroupTested || "",
        componentparametertested: disabledRow.TypeOfTest || "",
        testmethodspecification: disabledRow.ScopeStandard || "",
        groupid: disabledRow.Groupid ? Number(disabledRow.Groupid) : null,
        subgroupid: disabledRow.SubGroupid ? Number(disabledRow.SubGroupid) : null,
        isactive: 0,
        updatedBy: localStorage.getItem("orgId") || "0",
      };
      const response = await axios.put(
        `${API_BASE_URL}/admin-master/scope-master/${disabledRow.scopeid}`,
        payload,
      );
      if (response.data.status === "success") {
        setSuccessMessage("Scope disabled successfully!");
        fetchScopeList();
        fetchDisabledScopes();
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(response.data.message || "Failed to disabled scope");
      }
    } catch (err) {
      console.error("Error disabled scope:", err);
      setError(err.response?.data?.message || "Error disable scope");
    } finally {
      setLoading(false);
      setDeleteDialogOpen(false);
      setDisabledRow(null);
    }
  };
  // const handleDeleteConfirm = async () => {
  //   try {
  //     setLoading(true);
  //     const response = await axios.delete(
  //       `${API_BASE_URL}/admin-master/scope-master/${deleteId}`,
  //     );

  //     if (response.data.status === "success") {
  //       setSuccessMessage("Scope disabled successfully!");
  //       fetchScopeList();
  //       fetchDisabledScopes();
  //       setTimeout(() => setSuccessMessage(""), 3000);
  //     } else {
  //       setError(response.data.message || "Failed to disable scope");
  //     }
  //   } catch (err) {
  //     console.error("Error disabling scope:", err);
  //     setError(err.response?.data?.message || "Error disabling scope");
  //   } finally {
  //     setLoading(false);
  //     setDeleteDialogOpen(false);
  //     setDeleteId(null);
  //   }
  // };

  // Handle enable
  const handleEnableClick = (row) => {
    setEnableRow(row);
    setEnableDialogOpen(true);
  };

  const handleEnableConfirm = async () => {
    if (!enableRow) return;
    try {
      setLoading(true);
      const payload = {
        labtypeid: Number(enableRow.LabTypeid),
        disciplineid: Number(enableRow.disciplineid),
        disciplineName: enableRow.DisciplineName || "",
        scopetype: enableRow.Category,
        ptavailable: enableRow.PTAvailable,
        materialstested: enableRow.GroupTested || "",
        componentparametertested: enableRow.TypeOfTest || "",
        testmethodspecification: enableRow.ScopeStandard || "",
        groupid: enableRow.Groupid ? Number(enableRow.Groupid) : null,
        subgroupid: enableRow.SubGroupid ? Number(enableRow.SubGroupid) : null,
        isactive: 1,
        updatedBy: localStorage.getItem("orgId") || "0",
      };
      const response = await axios.put(
        `${API_BASE_URL}/admin-master/scope-master/${enableRow.scopeid}`,
        payload,
      );
      if (response.data.status === "success") {
        setSuccessMessage("Scope enabled successfully!");
        fetchScopeList();
        fetchDisabledScopes();
        setTimeout(() => setSuccessMessage(""), 3000);
      } else {
        setError(response.data.message || "Failed to enable scope");
      }
    } catch (err) {
      console.error("Error enabling scope:", err);
      setError(err.response?.data?.message || "Error enabling scope");
    } finally {
      setLoading(false);
      setEnableDialogOpen(false);
      setEnableRow(null);
    }
  };

  // Handle View Scope
  const handleViewScope = (scope) => {
    setSelectedScope(scope);
    setViewDialogOpen(true);
  };

  // Handle bulk upload
  const handleBulkUpload = () => {
    setBulkDialogOpen(true);
    setBulkDiscipline("");
    setBulkGroup("");
    setBulkSubGroup("");
    setBulkScopeType("");
    setBulkPTAvailable(false);
    setBulkFile(null);
    setBulkGroups([]);
  };

  const handleBulkDialogClose = () => {
    setBulkDialogOpen(false);
    setBulkDiscipline("");
    setBulkGroup("");
    setBulkSubGroup("");
    setBulkScopeType("");
    setBulkPTAvailable(false);
    setBulkFile(null);
    setBulkGroups([]);
  };

  // Bulk upload template download
  const handleDownloadTemplate = () => {
    if (!bulkLabType) {
      setError("Please select CAB Type to download template");
      return;
    }

    const headers = [
      "MaterialsTested",
      "ComponentParameterTested",
      "TestMethodSpecification",
    ];

    const ws = XLSX.utils.aoa_to_sheet([headers]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "ScopeTemplate");

    const cabType =
      LAB_TYPES.find((c) => c.id === bulkLabType)?.name || "scope";
    const fileName = `ScopeTemplate_${cabType}.xlsx`;

    XLSX.writeFile(wb, fileName);
  };

  // Parse Excel (.xlsx) or CSV file to validate headers and build payload
  const parseFile = async (file) => {
    const required = [
      "MaterialsTested",
      "ComponentParameterTested",
      "TestMethodSpecification",
    ];
    const ext = file.name.split(".").pop().toLowerCase();
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (evt) => {
        if (ext === "xlsx" || ext === "xls") {
          try {
            const data = new Uint8Array(evt.target.result);
            const workbook = XLSX.read(data, { type: "array" });
            const sheet = workbook.Sheets[workbook.SheetNames[0]];
            const json = XLSX.utils.sheet_to_json(sheet, { header: 1 });
            if (!json.length) return reject(new Error("File is empty"));
            const headers = json[0].map((h) => (h || "").toString().trim());
            const missing = required.filter((r) => !headers.includes(r));
            if (missing.length)
              return reject(
                new Error("Missing headers: " + missing.join(", ")),
              );
            const rows = json
              .slice(1)
              .map((rowArr) => {
                const obj = {};
                headers.forEach((h, idx) => {
                  obj[h] = rowArr[idx] ? rowArr[idx].toString().trim() : "";
                });
                return obj;
              })
              .filter((r) => r.MaterialsTested);
            resolve(rows);
          } catch (e) {
            reject(new Error("Invalid Excel file"));
          }
        } else {
          // CSV logic
          const text = evt.target.result;
          const lines = text.split(/\r?\n/).filter((l) => l.trim() !== "");
          if (lines.length === 0) return reject(new Error("File is empty"));
          const headers = lines[0].split(",").map((h) => h.trim());
          const missing = required.filter((r) => !headers.includes(r));
          if (missing.length)
            return reject(new Error("Missing headers: " + missing.join(", ")));
          const rows = lines
            .slice(1)
            .map((line) => {
              const cols = line.split(",");
              const obj = {};
              headers.forEach((h, idx) => {
                obj[h] = cols[idx] ? cols[idx].trim() : "";
              });
              return obj;
            })
            .filter((r) => r.MaterialsTested);
          resolve(rows);
        }
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      if (ext === "xlsx" || ext === "xls") {
        reader.readAsArrayBuffer(file);
      } else {
        reader.readAsText(file);
      }
    });
  };

  // Handle bulk upload submit
  const handleBulkUploadSubmit = async () => {
    setError("");
    setSuccessMessage("");
    if (!bulkLabType) {
      setError("Please select CAB Type");
      return;
    }
    if (!bulkDiscipline) {
      setError("Please select Discipline");
      return;
    }

    if ((bulkLabType === 1 || bulkLabType === 2) && !bulkGroup) {
      setError("Please select Group");
      return;
    }

    if (bulkLabType === 1 && !bulkSubGroup) {
      setError("Please select Sub-Group");
      return;
    }

    if (!bulkScopeType) {
      setError("Please select Scope Type");
      return;
    }
    if (!bulkFile) {
      setError("Please choose an Excel/CSV file to upload");
      return;
    }

    try {
      setUploadLoading(true);
      // Validate file headers and content
      const rows = await parseFile(bulkFile);
      if (!rows.length) {
        setError("No valid rows found in file");
        setUploadLoading(false);
        return;
      }

      let successCount = 0;
      let failCount = 0;
      let failRows = [];

      for (let i = 0; i < rows.length; i++) {
        const row = rows[i];
        // Prepare payload for each row using the dialog selections
        const payload = {
          labtypeid: bulkLabType,
          disciplineid: bulkDiscipline,
          disciplineName:
            disciplines.find((d) => Number(d.id) === Number(bulkDiscipline))
              ?.disciplineName || "",
          groupid: bulkGroup || null,
          subgroupid: bulkSubGroup || null,
          scopetype: bulkScopeType,
          ptavailable: bulkPTAvailable,
          materialstested: row.MaterialsTested,
          componentparametertested: row.ComponentParameterTested,
          testmethodspecification: row.TestMethodSpecification,
          updatedBy: localStorage.getItem("orgId") || "0",
        };

        try {
          const response = await axios.post(
            `${API_BASE_URL}/admin-master/scope-master`,
            payload,
          );
          if (response.data.status === "success") {
            successCount++;
          } else {
            failCount++;
            failRows.push({ row: i + 2, error: response.data.message });
          }
        } catch (err) {
          failCount++;
          failRows.push({
            row: i + 2,
            error: err.response?.data?.message || err.message,
          });
        }
      }

      setSuccessMessage(
        `Bulk upload complete. Success: ${successCount}, Failed: ${failCount}`,
      );
      if (failRows.length) {
        setError(
          "Failed rows: " +
            failRows.map((f) => `Row ${f.row}: ${f.error}`).join("; "),
        );
      }
      fetchScopeList();
      handleBulkDialogClose();
      setTimeout(() => setSuccessMessage(""), 4000);
    } catch (err) {
      console.error("Bulk upload error:", err);
      setError(
        err.response?.data?.message || err.message || "Bulk upload failed",
      );
    } finally {
      setUploadLoading(false);
    }
  };

  // Handle table pagination
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Handle filter changes
  const handleLabTypeFilterChange = (value) => {
    setFilterLabType(value);
    fetchDisciplines(value);
    setFilterDiscipline("");
    setFilterGroup("");
    setFilterSubGroup("");
    setPage(0);
  };

  const handleDisciplineFilterChange = (value) => {
    setFilterDiscipline(value);
    setFilterGroup("");
    setFilterSubGroup("");
    fetchGroups(filterLabType, value);
    setPage(0);
  };

  const handleGroupFilterChange = (value) => {
    setFilterGroup(value);
    setFilterSubGroup("");
    setPage(0);
  };

  const handleSubGroupFilterChange = (value) => {
    setFilterSubGroup(value);
    setPage(0);
  };

  const handleSearchChange = (event) => {
    setSearchText(event.target.value);
  };

  // Filter disciplines for bulk upload dialog
  const [bulkDisciplines, setBulkDisciplines] = useState([]);
  useEffect(() => {
    if (bulkLabType) {
      fetchDisciplines(bulkLabType).then(() => {
        // Use the same fetchDisciplines function
      });
    } else {
      setBulkDisciplines([]);
      setBulkDiscipline("");
    }
  }, [bulkLabType]);

  return (
    <Box sx={{ p: 3, backgroundColor: "#f5f5f5", minHeight: "100vh" }}>
      <Typography
        variant="h4"
        gutterBottom
        sx={{
          fontWeight: 600,
          color: "#1976d2",
          mb: 4,
        }}
      >
        Scope Master
      </Typography>

      {/* Alert Messages */}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}
      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage("")}
        >
          {successMessage}
        </Alert>
      )}

      {/* Form Section */}
      <Card elevation={3} sx={{ mb: 4 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
            {isEditMode ? "Edit Scope" : "Add New Scope"}
          </Typography>

          <form onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Laboratory Type (CAB Type) */}
              <Grid item xs={12} md={6} lg={3}>
                <Select
                  fullWidth
                  name="labtypeid"
                  value={formData.labtypeid}
                  onChange={handleInputChange}
                  displayEmpty
                  required
                  disabled={shouldDisableDropdown(filteredLabTypes)}
                  variant="outlined"
                >
                  <MenuItem value="" disabled>
                    <em>--Select CAB Type--</em>
                  </MenuItem>
                  {filteredLabTypes.length > 0 ? (
                    filteredLabTypes.map((cab) => (
                      <MenuItem key={cab.id} value={cab.id}>
                        {cab.name}
                      </MenuItem>
                    ))
                  ) : (
                    <MenuItem disabled>
                      <em>No Lab Types Available</em>
                    </MenuItem>
                  )}
                </Select>
              </Grid>

              {/* Discipline Name */}
              <Grid item xs={12} md={6} lg={3}>
                <Select
                  fullWidth
                  name="disciplineid"
                  value={formData.disciplineid}
                  onChange={handleInputChange}
                  displayEmpty
                  required
                  variant="outlined"
                  disabled={!formData.labtypeid}
                >
                  <MenuItem value="" disabled>
                    <em>--Select Discipline--</em>
                  </MenuItem>
                  {disciplines.map((discipline) => (
                    <MenuItem key={discipline.id} value={discipline.id}>
                      {discipline.disciplineName}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              {/* Group */}
              {(formData.labtypeid == 1 || formData.labtypeid == 2) && (
              <Grid item xs={12} md={6} lg={3}>
                <Select
                  fullWidth
                  name="groupid"
                  value={formData.groupid}
                  onChange={handleInputChange}
                  displayEmpty
                  variant="outlined"
                  disabled={!formData.disciplineid}
                >
                  <MenuItem value="">
                    <em>--Select Group--</em>
                  </MenuItem>
                  {groups.map((group) => (
                    <MenuItem key={group.grpId} value={group.grpId}>
                      {group.groupName}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              )}

              {/* Sub Group */}
              {(formData.labtypeid == 1) && (
              <Grid item xs={12} md={6} lg={3}>
                <Select
                  fullWidth
                  name="subgroupid"
                  value={formData.subgroupid}
                  onChange={handleInputChange}
                  displayEmpty
                  variant="outlined"
                  disabled={!formData.groupid}
                >
                  <MenuItem value="">
                    <em>--Select Sub Group--</em>
                  </MenuItem>
                  {subGroups.map((subGroup) => (
                    <MenuItem key={subGroup.sGrpId} value={subGroup.sGrpId}>
                      {subGroup.subGrpName}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>
              )}

              {/* Scope Type */}
              <Grid item xs={12} md={6} lg={3}>
                <Select
                  fullWidth
                  name="scopetype"
                  value={formData.scopetype}
                  onChange={handleInputChange}
                  displayEmpty
                  required
                  variant="outlined"
                >
                  <MenuItem value="" disabled>
                    <em>--Select Scope Type--</em>
                  </MenuItem>
                  {scopeTypeOptions.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              {/* PT Available */}
              <Grid item xs={12} md={6} lg={3}>
                <Typography variant="subtitle1" gutterBottom>
                  PT Available
                </Typography>
                <RadioGroup
                  row
                  name="ptavailable"
                  value={formData.ptavailable ? "true" : "false"}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      ptavailable: e.target.value === "true",
                    })
                  }
                >
                  <FormControlLabel
                    value="true"
                    control={<Radio />}
                    label="Yes"
                  />
                  <FormControlLabel
                    value="false"
                    control={<Radio />}
                    label="No"
                  />
                </RadioGroup>
              </Grid>

              {/* Materials or Products tested */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Materials or Products tested"
                  name="materialstested"
                  value={formData.materialstested}
                  onChange={handleInputChange}
                  variant="outlined"
                  multiline
                  rows={2}
                  placeholder="Enter materials or products tested"
                />
              </Grid>

              {/* Component, parameter or characteristic tested */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Component, parameter or characteristic tested / Specific Test"
                  name="componentparametertested"
                  value={formData.componentparametertested}
                  onChange={handleInputChange}
                  variant="outlined"
                  multiline
                  rows={3}
                  placeholder="Enter component, parameter, or characteristic tested"
                />
              </Grid>

              {/* Test Method Specification */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Test Method Specification against which tests are performed and / or the techniques / equipment used"
                  name="testmethodspecification"
                  value={formData.testmethodspecification}
                  onChange={handleInputChange}
                  variant="outlined"
                  multiline
                  rows={3}
                  placeholder="Enter test method specification"
                />
              </Grid>
            </Grid>

            {/* Form Buttons */}
            <Box sx={{ mt: 4, display: "flex", gap: 2 }}>
              <Button
                type="submit"
                variant="contained"
                startIcon={
                  loading ? <CircularProgress size={20} /> : <SaveIcon />
                }
                disabled={loading}
                sx={{ backgroundColor: "#1976d2" }}
              >
                {isEditMode ? "Update" : "Save"}
              </Button>

              <Button
                variant="outlined"
                onClick={handleReset}
                startIcon={<RefreshIcon />}
                disabled={loading}
              >
                Reset
              </Button>

              <Button
                variant="outlined"
                onClick={handleBulkUpload}
                startIcon={<UploadIcon />}
                disabled={loading}
              >
                Bulk Upload
              </Button>
            </Box>
          </form>
        </CardContent>
      </Card>

      {/* Scope Details Section */}
      <Paper elevation={3} sx={{ p: 3 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
          Scope Details
        </Typography>

        {/* Filters */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Test Code"
              value={filterTestCode}
              onChange={(e) => setFilterTestCode(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Component/Parameter Tested"
              value={filterComponent}
              onChange={(e) => setFilterComponent(e.target.value)}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Materials or Products Tested"
              value={filterMaterial}
              onChange={(e) => setFilterMaterial(e.target.value)}
              size="small"
            />
          </Grid>

          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              label="Test Method Specification"
              value={filterTestMethod}
              onChange={(e) => setFilterTestMethod(e.target.value)}
              size="small"
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              displayEmpty
              disabled={shouldDisableDropdown(filteredLabTypes)}
              value={filterLabType}
              onChange={(e) => handleLabTypeFilterChange(e.target.value)}
              size="small"
              renderValue={(selected) => {
                if (selected === "") {
                  return <em>--All CAB Types--</em>;
                }
                const cabType = LAB_TYPES.find(
                  (c) => String(c.id) === String(selected),
                );
                return cabType?.name || "Unknown";
              }}
            >
              <MenuItem value="">
                <em>--All CAB Types--</em>
              </MenuItem>
              {filteredLabTypes.length > 0 ? (
                filteredLabTypes.map((cab) => (
                  <MenuItem key={cab.id} value={cab.id}>
                    {cab.name}
                  </MenuItem>
                ))
              ) : (
                <MenuItem disabled>
                  <em>No Lab Types Available</em>
                </MenuItem>
              )}
            </Select>
          </Grid>

          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              displayEmpty
              value={filterDiscipline}
              onChange={(e) => handleDisciplineFilterChange(e.target.value)}
              disabled={!filterLabType}
              size="small"
              renderValue={(selected) => {
                if (selected === "") {
                  return <em>--All Disciplines--</em>;
                }
                const discipline = disciplines.find((d) => d.id === selected);
                return discipline?.disciplineName;
              }}
            >
              <MenuItem value="">
                <em>--All Disciplines--</em>
              </MenuItem>
              {disciplines.map((discipline) => (
                <MenuItem key={discipline.id} value={discipline.id}>
                  {discipline.disciplineName}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          { (filterLabType === 1 || filterLabType === 2) && (
            <>
          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              displayEmpty
              value={filterGroup}
              onChange={(e) => handleGroupFilterChange(e.target.value)}
              disabled={!filterLabType || !filterDiscipline}
              size="small"
              renderValue={(selected) => {
                if (selected === "") {
                  return <em>--All Groups--</em>;
                }
                const group = groups.find((g) => g.grpId === selected);
                return group?.groupName;
              }}
            >
              <MenuItem value="">
                <em>--All Groups--</em>
              </MenuItem>
              {groups.map((group) => (
                <MenuItem key={group.grpId} value={group.grpId}>
                  {group.groupName}
                </MenuItem>
              ))}
            </Select>
          </Grid>
          <Grid item xs={12} md={3}>
            <Select
              fullWidth
              displayEmpty
              value={filterSubGroup}
              onChange={(e) => handleSubGroupFilterChange(e.target.value)}
              disabled={!filterLabType || !filterDiscipline || !filterGroup}
              size="small"
              renderValue={(selected) => {
                if (selected === "") {
                  return <em>--All Sub Groups--</em>;
                }
                const subgroup = filterSubGroups.find((g) => String(g.sGrpId) === String(selected));
                return subgroup?.subGrpName || "N/A";
              }}
            >
              <MenuItem value="">
                <em>--All Sub Groups--</em>
              </MenuItem>
              {filterSubGroups.map((subgroup) => (
                <MenuItem key={subgroup.sGrpId} value={subgroup.sGrpId}>
                  {subgroup.subGrpName}
                </MenuItem>
              ))}
            </Select>
          </Grid>
            </>
          )}
          <Grid item xs={12} md={3}>
            <Button
              variant="outlined"
              fullWidth
              size="small"
              onClick={() => {
                const firstLabTypeId = getFirstLabTypeId();
                setFilterTestCode("");
                setFilterComponent("");
                setFilterMaterial("");
                setFilterTestMethod("");
                setFilterLabType(firstLabTypeId || "");
                setFilterDiscipline("");
                setFilterGroup("");
                setFilterSubGroup("");
                setPage(0);
              }}
              sx={{ height: "40px" }}
            >
              Clear Filters
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Button
              variant="contained"
              fullWidth
              startIcon={<DownloadIcon />}
              onClick={handleExportExcel}
              sx={{ height: "40px" }}
            >
              Export Excel
            </Button>
          </Grid>
        </Grid>

        {/* Results Counter */}
        {!loading && (
          <Box
            sx={{
              mb: 2,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography variant="body2" color="textSecondary">
              {totalCount > 0
                ? `Showing ${page * rowsPerPage + 1}-${Math.min((page + 1) * rowsPerPage, totalCount)} of ${totalCount} records`
                : "No records found"}
            </Typography>
            {(searchText || filterLabType || filterDiscipline) && (
              <Typography variant="body2" color="primary">
                Filters applied
              </Typography>
            )}
          </Box>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell>
                      <strong>S.no</strong>
                    </TableCell>
                    {filterLabType !== 1 && (
                      <TableCell>
                        <strong>CAB Type</strong>
                      </TableCell>
                    )}
                    <TableCell>
                      <strong>Test Code</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Discipline</strong>
                    </TableCell>
                     <TableCell>
                      <strong>Group / Sub-Group</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Component/Parameter Tested</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Materials or Products Tested</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Test Method Specification</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Scope Type</strong>
                    </TableCell>
                    <TableCell>
                      <strong>PT Available</strong>
                    </TableCell>
                    <TableCell>
                      <strong>No. of Labs</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Insert/Update Details</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Actions</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scopeData.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                        <Typography color="textSecondary">
                          {searchText || filterLabType || filterDiscipline
                            ? "No scope data found matching the current filters"
                            : "No scope data available"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    scopeData.map((row, index) => {
                      const cabType = LAB_TYPES.find(
                        (cab) => String(cab.id) === String(row.LabTypeid),
                      );

                      return (
                        <TableRow key={row.scopeid}>
                          <TableCell>
                            {page * rowsPerPage + index + 1}
                          </TableCell>
                          {filterLabType !== 1 && (
                          <TableCell>{cabType?.name || "N/A"}</TableCell>
                          )}
                          <TableCell>{row.TestCode || "N/A"}</TableCell>
                          <TableCell>{row.DisciplineName || "N/A"}</TableCell>
                          <TableCell>
                            {filterLabType === 1 ? (
                              <>
                                {row.GroupName || "N/A"}{" "}
                                <span
                                  style={{
                                    color: "green",
                                    fontWeight: 400,
                                  }}
                                >
                                  ({row.SubGroupName || "N/A"})
                                </span>
                              </>
                            ) : (
                              row.GroupName || "N/A"
                            )}
                          </TableCell>
                          <TableCell>{row.TypeOfTest || "N/A"}</TableCell>
                          <TableCell>{row.GroupTested || "N/A"}</TableCell>
                          <TableCell>{row.ScopeStandard || "N/A"}</TableCell>
                          <TableCell>{row.Category || "N/A"}</TableCell>
                          <TableCell>
                            <span
                              style={{
                                padding: "4px 8px",
                                borderRadius: "4px",
                                backgroundColor:
                                  row.PTAvailable === "1" ||
                                  row.PTAvailable === "Yes"
                                    ? "#c8e6c9"
                                    : "#ffcccc",
                                color:
                                  row.PTAvailable === "1" ||
                                  row.PTAvailable === "Yes"
                                    ? "#2e7d32"
                                    : "#c62828",
                              }}
                            >
                              {row.PTAvailable === "1" ||
                              row.PTAvailable === "Yes"
                                ? "Yes"
                                : "No"}
                            </span>
                          </TableCell>
                          <TableCell>
                              <Chip
                                label={row.LabCount || 0}
                                size="small"
                                color="primary"
                                variant="outlined"
                                clickable
                                onClick={() => handleViewLabs(row)}
                              />
                          </TableCell>
                          <TableCell>
                            <Typography>
                              Created On:
                              <b>
                                {" "}
                                {row.InsertDate
                                  ? new Date(
                                      row.InsertDate,
                                    ).toLocaleDateString()
                                  : "N/A"}
                              </b>
                            </Typography>
                            <Typography>
                              Inserted By:
                              <b>{row.insertedBy}</b>
                            </Typography>
                            <Typography>
                              Updated By:
                              <b>{row.updatedBy}</b>
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleEdit(row)}
                              title="Edit"
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleDeleteClick(row)}
                              title="Disable"
                            >
                              <BlockIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Pagination */}
            <TablePagination
              component="div"
              count={totalCount}
              page={page}
              onPageChange={handleChangePage}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{ borderTop: "1px solid #e0e0e0" }}
              showFirstButton
              showLastButton
            />
          </>
        )}
      </Paper>

      {/* Disabled Scopes Table */}
      <Paper elevation={3} sx={{ p: 3, mt: 4 }}>
        <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, mb: 3, color: "#c62828" }}>
          Disabled Scopes
        </Typography>
        {disabledScopeLoading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead sx={{ backgroundColor: "#fdecea" }}>
                <TableRow>
                  <TableCell><strong>S.No</strong></TableCell>
                  <TableCell><strong>Test Code</strong></TableCell>
                  <TableCell><strong>Discipline</strong></TableCell>
                  <TableCell><strong>Group / Sub-Group</strong></TableCell>
                  <TableCell><strong>Component/Parameter Tested</strong></TableCell>
                  <TableCell><strong>Materials or Products Tested</strong></TableCell>
                  <TableCell><strong>Test Method Specification</strong></TableCell>
                  <TableCell><strong>Scope Type</strong></TableCell>
                  <TableCell><strong>PT Available</strong></TableCell>
                  <TableCell><strong>Actions</strong></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {disabledScopeData.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                      <Typography color="textSecondary">No disabled scopes found</Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  disabledScopeData.map((row, index) => (
                    <TableRow key={row.scopeid} sx={{ backgroundColor: "#fff8f8" }}>
                      <TableCell>{index + 1}</TableCell>
                      <TableCell>{row.TestCode || "N/A"}</TableCell>
                      <TableCell>{row.DisciplineName || "N/A"}</TableCell>
                      <TableCell>
                        {row.GroupName || "N/A"}
                        {row.SubGroupName && (
                          <span style={{ color: "green", fontWeight: 400 }}>
                            {" "}({row.SubGroupName})
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{row.TypeOfTest || "N/A"}</TableCell>
                      <TableCell>{row.GroupTested || "N/A"}</TableCell>
                      <TableCell>{row.ScopeStandard || "N/A"}</TableCell>
                      <TableCell>{row.Category || "N/A"}</TableCell>
                      <TableCell>
                        <span
                          style={{
                            padding: "4px 8px",
                            borderRadius: "4px",
                            backgroundColor: row.PTAvailable === "1" || row.PTAvailable === "Yes" ? "#c8e6c9" : "#ffcccc",
                            color: row.PTAvailable === "1" || row.PTAvailable === "Yes" ? "#2e7d32" : "#c62828",
                          }}
                        >
                          {row.PTAvailable === "1" || row.PTAvailable === "Yes" ? "Yes" : "No"}
                        </span>
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleEnableClick(row)}
                          title="Enable"
                        >
                          <EnableIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Enable Confirmation Dialog */}
      <Dialog open={enableDialogOpen} onClose={() => setEnableDialogOpen(false)}>
        <DialogTitle>Confirm Enable</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to enable this scope?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEnableDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleEnableConfirm}
            color="success"
            variant="contained"
            disabled={loading}
          >
            Enable
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog
        open={bulkDialogOpen}
        onClose={handleBulkDialogClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Upload Scope</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} md={6}>
              <Select
                fullWidth
                displayEmpty
                disabled={shouldDisableDropdown(filteredLabTypes)}
                value={bulkLabType}
                onChange={(e) => {
                  setBulkLabType(e.target.value);
                  setBulkDiscipline("");
                  setBulkGroup("");
                  setBulkGroups([]);
                  setBulkSubGroup("");
                  setBulkSubGroups([]);
                }}
                renderValue={(selected) => {
                  if (!selected) return <em>--Select CAB Type--</em>;
                  return (
                    LAB_TYPES.find((c) => c.id === selected)?.name || "N/A"
                  );
                }}
              >
                <MenuItem value="">
                  <em>--Select CAB Type--</em>
                </MenuItem>
                {filteredLabTypes.length > 0 ? (
                  filteredLabTypes.map((c) => (
                    <MenuItem key={c.id} value={c.id}>
                      {c.name}
                    </MenuItem>
                  ))
                ) : (
                  <MenuItem disabled>
                    <em>No Lab Types Available</em>
                  </MenuItem>
                )}
              </Select>
            </Grid>
            <Grid item xs={12} md={6}>
              <Select
                fullWidth
                displayEmpty
                value={bulkDiscipline}
                onChange={(e) => {
                  setBulkDiscipline(e.target.value);
                  setBulkGroup("");
                }}
                disabled={!bulkLabType}
                renderValue={(selected) => {
                  if (!selected) return <em>--Select Discipline--</em>;
                  const d = disciplines.find((dd) => dd.id === selected);
                  return d?.disciplineName || "N/A";
                }}
              >
                <MenuItem value="">
                  <em>--Select Discipline--</em>
                </MenuItem>
                {disciplines.map((d) => (
                  <MenuItem key={d.id} value={d.id}>
                    {d.disciplineName}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            {(bulkLabType == 1 || bulkLabType == 2) && (
            <Grid item xs={12} md={12}>
              <Select
                fullWidth
                displayEmpty
                value={bulkGroup}
                onChange={(e) => setBulkGroup(e.target.value)}
                disabled={!bulkDiscipline}
                renderValue={(selected) => {
                  if (!selected) return <em>--Select Group--</em>;
                  const g = bulkGroups.find((gg) => gg.grpId === selected);
                  return g?.groupName || "N/A";
                }}
              >
                <MenuItem value="">
                  <em>--Select Group--</em>
                </MenuItem>
                {bulkGroups.map((group) => (
                  <MenuItem key={group.grpId} value={group.grpId}>
                    {group.groupName}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            )}

            {(bulkLabType == 1 || bulkLabType == 2) && (
            <Grid item xs={12} md={12}>
              <Select
                fullWidth
                displayEmpty
                value={bulkSubGroup}
                onChange={(e) => setBulkSubGroup(e.target.value)}
                disabled={!bulkDiscipline}
                renderValue={(selected) => {
                  if (!selected) return <em>--Select Sub Group--</em>;
                  const g = bulkSubGroups.find((gg) => gg.sGrpId === selected);
                  return g?.subGrpName || "N/A";
                }}
              >
                <MenuItem value="">
                  <em>--Select Sub Group--</em>
                </MenuItem>
                {bulkSubGroups.map((group) => (
                  <MenuItem key={group.sGrpId} value={group.sGrpId}>
                    {group.subGrpName}
                  </MenuItem>
                ))}
              </Select>
            </Grid>
            )}
            

            {/* Scope Type */}
            <Grid item xs={12} md={6}>
              <Select
                fullWidth
                displayEmpty
                value={bulkScopeType}
                onChange={(e) => setBulkScopeType(e.target.value)}
                disabled={!bulkDiscipline}
                renderValue={(selected) => {
                  if (!selected) return <em>--Select Scope Type--</em>;
                  return selected;
                }}
              >
                <MenuItem value="">
                  <em>--Select Scope Type--</em>
                </MenuItem>
                {scopeTypeOptions.map((option) => (
                  <MenuItem key={option} value={option}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </Grid>

            {/* PT Available */}
            <Grid item xs={12} md={6}>
              <Select
                fullWidth
                displayEmpty
                value={bulkPTAvailable || ""}
                onChange={(e) => setBulkPTAvailable(e.target.value)}
                disabled={!bulkDiscipline}
                renderValue={(selected) => {
                  if (selected === "Yes") return "PT Available: Yes";
                  if (selected === "No") return "PT Available: No";
                  return <em>--Select PT Available--</em>;
                }}
              >
                <MenuItem value="">
                  <em>--Select PT Available--</em>
                </MenuItem>
                <MenuItem value="Yes">Yes</MenuItem>
                <MenuItem value="No">No</MenuItem>
              </Select>
            </Grid>
            <Grid item xs={12}>
              <Button
                variant="outlined"
                onClick={handleDownloadTemplate}
                fullWidth
                startIcon={<DownloadIcon />}
              >
                Download Template
              </Button>
            </Grid>
            <Grid item xs={12}>
              <input
                accept=".xlsx,.xls,.csv"
                style={{ display: "block" }}
                id="bulk-file-input"
                type="file"
                onChange={(e) => setBulkFile(e.target.files[0])}
              />
              {bulkFile && (
                <Typography variant="body2" sx={{ mt: 1 }}>
                  {bulkFile.name}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                Note: The selected CAB Type, Discipline, Group, Scope Type and
                PT Available above will be applied to all rows in the uploaded
                file. Excel should contain: MaterialsTested,
                ComponentParameterTested, TestMethodSpecification.
              </Typography>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleBulkDialogClose} disabled={uploadLoading}>
            Cancel
          </Button>
          <Button
            onClick={handleBulkUploadSubmit}
            variant="contained"
            disabled={
              uploadLoading ||
              !bulkFile ||
              !bulkLabType ||
              !bulkDiscipline ||
              !bulkScopeType
            }
            startIcon={
              uploadLoading ? <CircularProgress size={18} /> : <UploadIcon />
            }
          >
            Upload
          </Button>
        </DialogActions>
      </Dialog>

      {/* View Scope Dialog */}
      <Dialog
        open={viewDialogOpen}
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Scope Details</DialogTitle>
        <DialogContent dividers>
          {selectedScope && (
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  CAB Type:
                </Typography>
                <Typography variant="body1">
                  {selectedScope.LabTypeName || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Discipline:
                </Typography>
                <Typography variant="body1">
                  {selectedScope.DisciplineName || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Scope Type:
                </Typography>
                <Typography variant="body1">
                  {selectedScope.Category || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  PT Available:
                </Typography>
                <Typography variant="body1">
                  {selectedScope.PTAvailable === "1" ||
                  selectedScope.PTAvailable === "Yes"
                    ? "Yes"
                    : "No"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Component/Parameter Tested:
                </Typography>
                <Typography variant="body1">
                  {selectedScope.TypeOfTest || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Materials or Products Tested:
                </Typography>
                <Typography variant="body1">
                  {selectedScope.GroupTested || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" color="textSecondary">
                  Test Method Specification:
                </Typography>
                <Typography variant="body1">
                  {selectedScope.ScopeStandard || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Group:
                </Typography>
                <Typography variant="body1">
                  {selectedScope.GroupName || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Sub Group:
                </Typography>
                <Typography variant="body1">
                  {selectedScope.SubGroupadd || "N/A"}
                </Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="textSecondary">
                  Created On:
                </Typography>
                <Typography variant="body1">
                  {selectedScope.InsertDate
                    ? new Date(selectedScope.InsertDate).toLocaleString()
                    : "N/A"}
                </Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Labs List Dialog */}
      <Dialog
        open={labsDialogOpen}
        onClose={() => setLabsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Labs using scope:{" "}
          <strong>{labsDialogScope?.TestCode || ""}</strong>
          {!labsListLoading && ` (${labsList.length} labs)`}
        </DialogTitle>
        <DialogContent dividers>
          {labsListLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
                  <TableRow>
                    <TableCell><strong>S.No</strong></TableCell>
                    <TableCell><strong>Lab ID</strong></TableCell>
                    <TableCell><strong>Application No</strong></TableCell>
                    <TableCell><strong>Laboratory Name</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {labsList.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={4} align="center">
                        <Typography color="textSecondary">
                          No labs found for this scope
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    labsList.map((lab, index) => (
                      <TableRow key={index}>
                        <TableCell>{index + 1}</TableCell>
                        <TableCell>{lab.LabId || "-"}</TableCell>
                        <TableCell>{lab.ApplicationNo || "-"}</TableCell>
                        <TableCell>{lab.LaboratoryName || "-"}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLabsDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
      >
        <DialogTitle>Confirm Disable</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to disable this scope?</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={loading}
          >
            Disable
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ScopeMaster;